import { useLayoutEffect, useState } from "react";
import {
  canRenderBatchPreview,
  createBatchConnectionPreviewPaths,
  type PreviewPoint
} from "./batch-connection-preview.js";

export interface BatchConnectionPreviewProps {
  canvasElement: HTMLElement | null;
  sourceNodeIds: readonly string[];
  client: PreviewPoint;
}

interface MeasuredSource {
  nodeId: string;
  point: PreviewPoint;
}

function sourceSelector(nodeId: string): string {
  return `.react-flow__node[data-id="${CSS.escape(nodeId)}"] .react-flow__handle.source`;
}

export function BatchConnectionPreview({
  canvasElement,
  sourceNodeIds,
  client
}: BatchConnectionPreviewProps) {
  const [sources, setSources] = useState<MeasuredSource[]>([]);

  useLayoutEffect(() => {
    if (!canvasElement) {
      setSources([]);
      return;
    }
    const canvas = canvasElement.getBoundingClientRect();
    setSources(sourceNodeIds.flatMap((nodeId) => {
      const handle = canvasElement.querySelector<HTMLElement>(sourceSelector(nodeId));
      if (!handle) return [];
      const bounds = handle.getBoundingClientRect();
      return [{
        nodeId,
        point: {
          x: bounds.left - canvas.left + bounds.width / 2,
          y: bounds.top - canvas.top + bounds.height / 2
        }
      }];
    }));
  }, [canvasElement, sourceNodeIds]);

  if (!canvasElement || !canRenderBatchPreview(sources)) return null;
  const canvas = canvasElement.getBoundingClientRect();
  const paths = createBatchConnectionPreviewPaths({
    sources,
    target: { x: client.x - canvas.left, y: client.y - canvas.top }
  });
  return (
    <svg className="studio-batch-connection-preview" aria-hidden="true">
      {paths.map((path) => <path key={path.sourceNodeId} data-source-node-id={path.sourceNodeId} d={path.d} />)}
    </svg>
  );
}
